/**
 * The browser half of the cloud-storage connect flow, shared by both providers: a raw
 * authorization-code popup that returns to the static `public/cloud-oauth-callback.html`, which
 * posts the one-time code back here. The code goes straight to `connectCloudStorage`; the refresh
 * token it buys never leaves the server.
 */

import {v4 as uuidv4} from 'uuid';

export type CloudProvider = 'GOOGLE_DRIVE' | 'ONEDRIVE';

/** One row of `viewer.cloudStorageProviders`: public values only, never a secret. */
export type CloudProviderConfig = {
	provider: CloudProvider;
	clientId: string;
	/** Google only. */
	pickerApiKey: string | null;
	/** Google only (Cloud project number). */
	appId: string | null;
	/** The shared callback page the consent popup returns to. */
	redirectUri: string;
	connection: {accountEmail: string; status: string} | null;
};

/** What a provider picker hands back. `externalFileId`: Google file id; OneDrive `${driveId}/${itemId}`. */
export type PickedFile = {externalFileId: string; name: string};
export type PickedCloudFile = PickedFile & {provider: CloudProvider};

/** The GraphQL enum → the message-key / portal `source` segment. */
export const PROVIDER_KEY: Record<CloudProvider, string> = {GOOGLE_DRIVE: 'google_drive', ONEDRIVE: 'onedrive'};
/** The portal asset `source` → the GraphQL enum; `native` (and anything else) has no provider. */
export const PROVIDER_BY_SOURCE: Partial<Record<string, CloudProvider>> = {
	google_drive: 'GOOGLE_DRIVE',
	onedrive: 'ONEDRIVE',
};

/** The `type` the callback page stamps on its message. */
export const OAUTH_MESSAGE_TYPE = 'forecast-cloud-oauth';

// Rejection reasons. The two failures are translation ids, so a caller can show them as they are;
// a cancel is the user's own choice and has nothing to say.
export const CANCELLED = 'CLOUD_CANCELLED';
export const CONNECT_FAILED = 'client_assets.error.cloud_connect_failed';
export const PICKER_FAILED = 'client_assets.error.cloud_picker_failed';

type AuthorizeRequest = {url: string; params: Record<string, string>};

// prompt=consent on both: Google only returns a refresh token on a fresh consent, and
// Microsoft's .default only prompts for a resource nothing was granted for yet. openid email on
// both, because the server reads the account email from the id_token.
const AUTHORIZE: Record<CloudProvider, AuthorizeRequest> = {
	GOOGLE_DRIVE: {
		url: 'https://accounts.google.com/o/oauth2/v2/auth',
		params: {
			scope: 'https://www.googleapis.com/auth/drive.file openid email',
			access_type: 'offline',
			prompt: 'consent',
		},
	},
	// Work/school accounts only in v1.
	ONEDRIVE: {
		url: 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize',
		params: {
			scope: 'https://graph.microsoft.com/.default offline_access openid email',
			prompt: 'consent',
		},
	},
};

const POPUP_FEATURES = 'popup=yes,width=520,height=680';
export const POPUP_POLL_MS = 500;
// A popup can close in the same tick its message is queued; give the message a beat to land.
const POPUP_CLOSED_GRACE_MS = 500;

// The callback page posts back to its own origin and only same-origin messages are accepted, so a
// redirect URI on any other origin (another app host, a preview) could never answer.
const isSameOrigin = (uri: string): boolean => {
	try {
		return new URL(uri).origin === window.location.origin;
	} catch {
		return false;
	}
};

type OAuthMessage = {type?: unknown; code?: unknown; state?: unknown; error?: unknown};

/**
 * Opens the provider's consent popup and resolves the authorization code its callback posts back.
 * Call it straight from the click handler, with no await before it: the popup is opened
 * synchronously, inside the user gesture, or a popup blocker eats it.
 * Rejects with CANCELLED (denied, or the popup closed) or CONNECT_FAILED.
 */
export const requestCloudCode = (config: CloudProviderConfig): Promise<string> => {
	if (!isSameOrigin(config.redirectUri)) return Promise.reject(new Error(CONNECT_FAILED));
	const state = uuidv4();
	const authorize = AUTHORIZE[config.provider];
	const url = new URL(authorize.url);
	Object.entries({
		client_id: config.clientId,
		response_type: 'code',
		redirect_uri: config.redirectUri,
		state,
		...authorize.params,
	}).forEach(([key, value]) => url.searchParams.set(key, value));
	const popup = window.open(url.toString(), OAUTH_MESSAGE_TYPE, POPUP_FEATURES);
	if (!popup) return Promise.reject(new Error(CONNECT_FAILED));

	return new Promise((resolve, reject) => {
		const timers: {poll?: ReturnType<typeof setInterval>; closed?: ReturnType<typeof setTimeout>} = {};
		let stopListening = () => undefined as void;
		const settle = (finish: () => void) => {
			stopListening();
			clearInterval(timers.poll);
			clearTimeout(timers.closed);
			finish();
		};
		// Only the callback page we opened, on our own origin, answering this request.
		const onMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin || event.source !== popup) return;
			const data = (event.data ?? {}) as OAuthMessage;
			if (data.type !== OAUTH_MESSAGE_TYPE) return;
			if (data.state !== state) return settle(() => reject(new Error(CONNECT_FAILED)));
			if (data.error) {
				const reason = data.error === 'access_denied' ? CANCELLED : CONNECT_FAILED;
				return settle(() => reject(new Error(reason)));
			}
			if (typeof data.code !== 'string' || !data.code) return settle(() => reject(new Error(CONNECT_FAILED)));
			const code = data.code;
			settle(() => resolve(code));
		};
		window.addEventListener('message', onMessage);
		stopListening = () => window.removeEventListener('message', onMessage);
		timers.poll = setInterval(() => {
			if (!popup.closed) return;
			clearInterval(timers.poll);
			timers.closed = setTimeout(() => settle(() => reject(new Error(CANCELLED))), POPUP_CLOSED_GRACE_MS);
		}, POPUP_POLL_MS);
	});
};

const scripts = new Map<string, Promise<void>>();

/**
 * Injects `<script async src>` once per src and resolves when it has loaded. A failed load is
 * forgotten (and its tag removed), so a later call retries.
 */
export const loadScript = (src: string): Promise<void> => {
	const known = scripts.get(src);
	if (known) return known;
	const loaded = new Promise<void>((resolve, reject) => {
		const tag = document.createElement('script');
		tag.src = src;
		tag.async = true;
		tag.addEventListener('load', () => resolve());
		tag.addEventListener('error', () => {
			scripts.delete(src);
			tag.remove();
			reject(new Error('SCRIPT_LOAD_FAILED'));
		});
		document.head.appendChild(tag);
	});
	scripts.set(src, loaded);
	return loaded;
};
